/**
 * Cart Preorder Badge Handler
 * Checks inventory levels via Storefront API and displays preorder badges for items with zero inventory
 */
class CartPreorderChecker {
    constructor() {
        this.storefrontToken = this.getStorefrontToken();
        this.shopDomain = Shopify.shop;
        this.badgeText = this.getBadgeText();
        this.checkedVariants = new Set();

        if (!this.storefrontToken) {
            console.warn(
                "Storefront API token not configured. Preorder badges will not be displayed.",
            );
            return;
        }

        this.init();
    }

    /**
     * Get Storefront API token from theme settings
     */
    getStorefrontToken() {
        // Try to get from window or meta tag
        if (
            window.theme &&
            window.theme.settings &&
            window.theme.settings.storefront_api_token
        ) {
            return window.theme.settings
                .storefront_api_token;
        }

        // Try to get from meta tag
        const metaTag = document.querySelector(
            'meta[name="storefront-api-token"]',
        );
        if (metaTag) {
            return metaTag.content;
        }

        return null;
    }

    /**
     * Get preorder badge text from translations
     */
    getBadgeText() {
        if (
            window.theme &&
            window.theme.translations &&
            window.theme.translations.preorder
        ) {
            return window.theme.translations.preorder;
        }
        return "Pre-Order"; // Fallback
    }

    /**
     * Initialize the checker
     */
    init() {
        // Check cart items on page load
        this.checkCartItems();

        // Listen for cart updates
        document.addEventListener("cart:updated", () => {
            this.checkCartItems();
        });

        // Listen for cart refresh events
        document.addEventListener("cart:refresh", () => {
            this.checkCartItems();
        });

        // Observe DOM changes for dynamically loaded cart items
        this.observeCartChanges();
    }

    /**
     * Observe cart DOM changes
     */
    observeCartChanges() {
        const cartObserver = new MutationObserver(
            (mutations) => {
                let shouldCheck = false;

                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach(
                            (node) => {
                                if (
                                    node.nodeType === 1 &&
                                    (node.classList?.contains(
                                        "product-cart-item",
                                    ) ||
                                        node.querySelector?.(
                                            ".product-cart-item",
                                        ))
                                ) {
                                    shouldCheck = true;
                                }
                            },
                        );
                    }
                });

                if (shouldCheck) {
                    this.checkCartItems();
                }
            },
        );

        // Observe cart drawer
        const cartDrawer =
            document.querySelector(".cart-drawer");
        if (cartDrawer) {
            cartObserver.observe(cartDrawer, {
                childList: true,
                subtree: true,
            });
        }

        // Observe main cart
        const mainCart = document.querySelector(
            ".thb-cart-form",
        );
        if (mainCart) {
            cartObserver.observe(mainCart, {
                childList: true,
                subtree: true,
            });
        }
    }

    /**
     * Check all cart items for inventory levels
     */
    async checkCartItems() {
        try {
            // Get current cart
            const cartResponse = await fetch("/cart.js");
            const cart = await cartResponse.json();

            if (!cart.items || cart.items.length === 0) {
                return;
            }

            // Add index to each item
            cart.items.forEach((item, i) => {
                item.index = i;
            });

            // Batch fetch inventory and tags for all items in a single GraphQL request
            const variantIds = cart.items.map((item) =>
                this.convertToGid(item.variant_id),
            );
            const inventoryDataMap =
                await this.fetchMultipleVariantsInventory(
                    variantIds,
                );

            // Process each item with the fetched inventory data
            for (const item of cart.items) {
                const variantId = this.convertToGid(
                    item.variant_id,
                );
                const inventoryData =
                    inventoryDataMap.get(variantId);

                if (inventoryData) {
                    // Add tags from GraphQL response to item
                    if (inventoryData.product?.tags) {
                        item.tags =
                            inventoryData.product.tags;
                    }

                    // Cache the inventory data
                    if (!this.inventoryCache) {
                        this.inventoryCache = new Map();
                    }
                    this.inventoryCache.set(
                        variantId,
                        inventoryData,
                    );
                    this.checkedVariants.add(variantId);

                    // Check if should show preorder badge
                    const shouldAlwaysShowPreorder =
                        this.shouldShowPreorderByName(item);

                    // Add badge if: 1) out of stock (quantityAvailable is a number <= 0, not null), OR 2) has streetwear tag + (Essentials/Stussy/Adidas Tang)
                    if (
                        (inventoryData.quantityAvailable !== null &&
                            inventoryData.quantityAvailable <= 0) ||
                        shouldAlwaysShowPreorder
                    ) {
                        this.addPreorderBadge(item);
                    }
                }
            }
        } catch (error) {
            console.error(
                "Error checking cart items:",
                error,
            );
        }
    }

    /**
     * Check if product should show preorder badge by name and tags
     * Logic: Must have streetwear tag AND (stussy in name)
     */
    shouldShowPreorderByName(item) {
        const productTitle =
            item.product_title?.toLowerCase() || "";

        // Get product tags - try both possible field names
        const productTags =
            item.tags || item.product_tags || [];

        // Check if has streetwear tag
        let hasStreetWearTag = false;

        if (Array.isArray(productTags)) {
            hasStreetWearTag = productTags.some(
                (tag) => tag.toLowerCase() === "streetwear",
            );
        } else if (typeof productTags === "string") {
            // If tags is a comma-separated string, split it
            hasStreetWearTag = productTags
                .toLowerCase()
                .split(",")
                .map((tag) => tag.trim())
                .includes("streetwear");
        }

        // If no streetwear tag, don't show preorder badge
        if (!hasStreetWearTag) {
            return false;
        }

        // Has streetwear tag - check if it's Stussy (always preorder)
        if (productTitle.includes("stussy")) {
            return true;
        }

        return false;
    }

    /**
     * Check individual item inventory (used for re-adding badges from cache)
     */
    checkItemInventoryFromCache(item) {
        const variantId = this.convertToGid(
            item.variant_id,
        );

        try {
            // Check if we already have inventory data cached
            if (this.checkedVariants.has(variantId)) {
                // Use cached data
                const inventoryData =
                    this.inventoryCache?.get(variantId);

                // Get tags from cache and add to item
                if (inventoryData?.product?.tags) {
                    item.tags = inventoryData.product.tags;
                }

                // Check if should show preorder badge
                const shouldAlwaysShowPreorder =
                    this.shouldShowPreorderByName(item);

                if (
                    inventoryData &&
                    ((inventoryData.quantityAvailable !== null &&
                        inventoryData.quantityAvailable <= 0) ||
                        shouldAlwaysShowPreorder)
                ) {
                    this.addPreorderBadge(item);
                }
            }
        } catch (error) {
            console.error(
                `Error checking inventory for variant ${item.variant_id}:`,
                error,
            );
        }
    }

    /**
     * Convert numeric variant ID to GraphQL Global ID
     */
    convertToGid(variantId) {
        return `gid://shopify/ProductVariant/${variantId}`;
    }

    /**
     * Fetch inventory and tags for multiple variants in a single GraphQL request
     */
    async fetchMultipleVariantsInventory(variantGids) {
        // Build GraphQL query with multiple nodes
        const query = `
      query getMultipleVariantsInventory($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            quantityAvailable
            currentlyNotInStock
            product {
              tags
            }
          }
        }
      }
    `;

        const response = await fetch(
            `https://${this.shopDomain}/api/2024-01/graphql.json`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token":
                        this.storefrontToken,
                },
                body: JSON.stringify({
                    query: query,
                    variables: { ids: variantGids },
                }),
            },
        );

        if (!response.ok) {
            throw new Error(
                `Storefront API request failed: ${response.status}`,
            );
        }

        const result = await response.json();

        if (result.errors) {
            throw new Error(
                `GraphQL errors: ${JSON.stringify(result.errors)}`,
            );
        }

        // Convert array response to Map for easy lookup
        const inventoryMap = new Map();
        if (result.data?.nodes) {
            result.data.nodes.forEach((node) => {
                if (node && node.id) {
                    inventoryMap.set(node.id, node);
                }
            });
        }

        return inventoryMap;
    }

    /**
     * Fetch variant inventory and product tags from Storefront API (single variant)
     * @deprecated Use fetchMultipleVariantsInventory for better performance
     */
    async fetchVariantInventory(variantGid) {
        const query = `
      query getVariantInventory($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            quantityAvailable
            currentlyNotInStock
            product {
              tags
            }
          }
        }
      }
    `;

        const response = await fetch(
            `https://${this.shopDomain}/api/2024-01/graphql.json`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token":
                        this.storefrontToken,
                },
                body: JSON.stringify({
                    query: query,
                    variables: { id: variantGid },
                }),
            },
        );

        if (!response.ok) {
            throw new Error(
                `Storefront API request failed: ${response.status}`,
            );
        }

        const result = await response.json();

        if (result.errors) {
            throw new Error(
                `GraphQL errors: ${JSON.stringify(result.errors)}`,
            );
        }

        return result.data?.node;
    }

    /**
     * Add preorder badge to cart item
     */
    addPreorderBadge(item) {
        // Find cart item elements (both in main cart and cart drawer)
        // Note: Shopify uses 1-based indexing in Liquid (item.index | plus: 1)
        const cartItemSelectors = [
            `#CartItem-${item.index + 1}`,
            `#CartDrawerItem-${item.index + 1}`,
        ];

        cartItemSelectors.forEach((selector) => {
            const cartItem =
                document.querySelector(selector);

            if (!cartItem) {
                return;
            }

            // Check if badge or wrapper already exists - if so, skip to avoid duplicates
            const existingBadge = cartItem.querySelector(
                ".cart-preorder-badge",
            );
            const existingWrapper = cartItem.querySelector(
                ".cart-item-options-wrapper",
            );

            if (existingBadge || existingWrapper) {
                return;
            }

            // Create badge element (clickable link to preorder info page)
            const badge = document.createElement("a");
            badge.className = "cart-preorder-badge";
            badge.href = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'pages/pre-order';
            badge.textContent = this.badgeText;

            // Find the best place to insert the badge
            const infoContainer = cartItem.querySelector(
                ".product-cart-item-info",
            );
            const optionsContainer = cartItem.querySelector(
                ".product-cart-item-options",
            );
            const quantitySelector = cartItem.querySelector(
                "quantity-selector",
            );
            const priceSpan =
                cartItem.querySelector(".price");

            if (infoContainer) {
                // Priority 1: Insert alongside product-cart-item-options if it exists
                if (optionsContainer) {
                    // Create a wrapper div with flex layout
                    const wrapper =
                        document.createElement("div");
                    wrapper.className =
                        "cart-item-options-wrapper";
                    wrapper.style.display = "flex";
                    wrapper.style.alignItems = "center";
                    wrapper.style.gap = "12px";
                    wrapper.style.flexWrap = "wrap";

                    // Move optionsContainer into wrapper
                    const parent =
                        optionsContainer.parentNode;
                    const nextSibling =
                        optionsContainer.nextSibling;
                    wrapper.appendChild(optionsContainer);
                    wrapper.appendChild(badge);

                    // Insert wrapper where optionsContainer was
                    parent.insertBefore(
                        wrapper,
                        nextSibling,
                    );
                } else if (priceSpan) {
                    // Priority 2: Insert after price span
                    priceSpan.parentNode.insertBefore(
                        badge,
                        priceSpan.nextSibling,
                    );
                } else {
                    // Priority 3: Insert after product link, before quantity selector
                    const productLink =
                        infoContainer.querySelector(
                            ".cart-product-link",
                        );
                    if (productLink && quantitySelector) {
                        productLink.parentNode.insertBefore(
                            badge,
                            quantitySelector,
                        );
                    } else if (productLink) {
                        productLink.parentNode.insertBefore(
                            badge,
                            productLink.nextSibling,
                        );
                    }
                }
            } else {
                // Fallback: find the product name cell/container and append
                const productNameCell =
                    cartItem.querySelector(
                        ".product-name, .product-cart-item",
                    );
                if (productNameCell) {
                    const targetContainer =
                        productNameCell.querySelector(
                            "div",
                        ) || productNameCell;
                    if (
                        quantitySelector &&
                        quantitySelector.parentNode ===
                            targetContainer
                    ) {
                        targetContainer.insertBefore(
                            badge,
                            quantitySelector,
                        );
                    } else {
                        targetContainer.appendChild(badge);
                    }
                }
            }
        });
    }

    /**
     * Reset checked variants (useful when cart is cleared)
     */
    reset() {
        this.checkedVariants.clear();
        if (this.inventoryCache) {
            this.inventoryCache.clear();
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        window.CartPreorderChecker =
            new CartPreorderChecker();
    });
} else {
    window.CartPreorderChecker = new CartPreorderChecker();
}

// Reset on cart clear
document.addEventListener("cart:cleared", () => {
    if (window.CartPreorderChecker) {
        window.CartPreorderChecker.reset();
    }
});
